import { useQuery, useQueryClient } from "@tanstack/react-query"
import type {
  AdminBrandingSettings,
  AdminFreeProduct,
  AdminProduct,
  AdminProductCatalog,
  AdminProductGuild,
} from "@ternary/contracts"
import {
  Alert,
  Button,
  breakable,
  Checkbox,
  Dialog,
  EmptyState,
  Field,
  FormWizard,
  Input,
  MultiSelect,
  PageHeader,
  Radio,
  Select,
  Textarea,
  useToast,
  type WizardErrors,
  WizardFieldError,
  type WizardStep,
  type WizardStepContext,
} from "@ternary/ui"
import { type ReactNode, useEffect, useId, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  AdminApiError,
  createAdminProduct,
  loadAdminProducts,
  loadAdminSettings,
  updateAdminBranding,
  updateAdminProduct,
} from "../api.js"
import { CreateRoleField } from "../components/create-role-field.js"
import {
  initialStandardLegal,
  isLegalLink,
  type LegalDraft,
  LegalLinks,
  LINK_MESSAGE,
  legalSaveFields,
} from "../components/legal-links.js"
import { NavIcon } from "../components/nav-icons.js"
import { ogAdminCurrencies } from "../components/og-admin-currencies.generated.js"
import { SignupFieldsEditor } from "../components/signup-fields-editor.js"
import {
  currencySymbol,
  draftFromFreeProduct,
  draftFromPaidProduct,
  formatMoney,
  freeRequest,
  isReachable,
  lines,
  newProductDraft,
  type PricingType,
  type ProductDraft,
  paidPriceLabel,
  paidRequest,
  SLUG_TAKEN,
  signupPathPrefix,
  signupUrl,
  slugFromName,
  validateAccess,
  validateBasics,
  validateBilling,
  validateExtras,
  validateFields,
  validatePrice,
  validatePricing,
} from "../product-draft.js"

/** A new product's draft and submits, kept in sessionStorage so a reload resumes them. */
export const PRODUCT_DRAFT_KEY = "tny_product_draft"

type Editing = { kind: "free"; product: AdminFreeProduct } | { kind: "paid"; product: AdminProduct }

type StepProps = WizardStepContext<ProductDraft> & {
  catalog: AdminProductCatalog
  editing: Editing | null
}

const PRICING: { value: PricingType; label: string; hint: string }[] = [
  {
    value: "free",
    label: "Free",
    hint: "A signup page with no price.",
  },
  { value: "one_time", label: "One-time", hint: "Members pay once for lasting access." },
  {
    value: "recurring",
    label: "Recurring",
    hint: "Weekly, monthly or yearly, with an optional free trial.",
  },
]

// Each period's name after a count of 1, then after any other count.
const INTERVALS = [
  { value: "day", one: "Day", label: "Days" },
  { value: "week", one: "Week", label: "Weeks" },
  { value: "month", one: "Month", label: "Months" },
  { value: "year", one: "Year", label: "Years" },
] as const

/**
 * The step's errors, less those of the fields edited since the errors came: editing a field takes
 * its error away until the next Continue checks it again.
 */
function useEditedErrors(errors: WizardErrors) {
  const [edited, setEdited] = useState<{ from: WizardErrors; keys: string[] }>({
    from: errors,
    keys: [],
  })
  const keys = edited.from === errors ? edited.keys : []
  return {
    shown: (key: string) => (keys.includes(key) ? undefined : errors[key]),
    clear: (...names: string[]) => setEdited({ from: errors, keys: [...keys, ...names] }),
  }
}

function numberValue(text: string) {
  return Number.parseInt(text.trim() || "0", 10)
}

/**
 * The draft on another pricing type. Free and paid roles come from different servers, and the
 * slug means different things: a free product's signup link, a paid one's storefront password,
 * which hides it. So a crossing starts both again, the slug from the name, as if never edited.
 */
function withPricing(draft: ProductDraft, pricingType: PricingType): ProductDraft {
  const crossing = (draft.pricingType === "free") !== (pricingType === "free")
  return crossing
    ? {
        ...draft,
        noRoles: false,
        pricingType,
        removalRoleIds: [],
        roleIds: [],
        slug: slugFromName(draft.name),
        slugEdited: false,
        trialRoleIds: [],
      }
    : { ...draft, pricingType }
}

/** The way to connect or reconnect the Discord bot. */
function ConnectDiscordLink() {
  return <Link to="/dashboard/discord">Connect Discord</Link>
}

/**
 * The publish error for missing terms and privacy links, with the way to add them. Basics' own
 * legal fields (`BasicsLegal`) come first while the standard terms are available; this stays as
 * the fallback.
 */
function LegalLinksError({ message }: { message: string }) {
  return (
    <p className="tny-error admin-inline-links" role="alert">
      {message} <Link to="/dashboard/settings?tab=legal">Open Settings</Link>
    </p>
  )
}

/**
 * Publish's legal refusal no longer stands: Basics' legal dialog (G5) has since made legal ready.
 * Only while the standard terms are available, so with the setting off the error shows as today.
 */
function legalSettled(catalog: AdminProductCatalog) {
  return catalog.legal.standardAvailable && catalog.legal.ready
}

type LegalErrors = { privacyUrl?: string | undefined; termsUrl?: string | undefined }

/**
 * A signup page needs both links (admin-api's `hasSignupLegalLinks`), unless Ternary's standard
 * terms replace them.
 */
function legalErrors(draft: LegalDraft): LegalErrors {
  if (draft.standardLegal) return {}
  const errors: LegalErrors = {}
  for (const key of ["termsUrl", "privacyUrl"] as const) {
    if (!isLegalLink(draft[key].trim())) errors[key] = LINK_MESSAGE
  }
  return errors
}

/** The saved legal, as the dialog opens on it. */
function legalDraftOf(settings: AdminBrandingSettings): LegalDraft {
  return {
    privacyUrl: settings.privacyUrl,
    standardLegal: initialStandardLegal(settings, true),
    termsUrl: settings.termsUrl,
  }
}

/**
 * Basics' legal (G5), shown only while a signup page could not publish and Ternary's standard
 * terms are available: the standard terms or the business's own links, saved through the branding
 * update, so the owner never leaves the draft. Until Branding loads, or for an account that can't
 * change it, nothing shows, and Publish's error with its link to Settings is the way.
 */
function BasicsLegal() {
  const query = useQuery({ queryFn: loadAdminSettings, queryKey: ["admin-settings"] })
  return query.data?.writable ? <BasicsLegalChoice settings={query.data} /> : null
}

/**
 * A line on Basics and a dialog with the legal choice and Save: a dialog, so Basics keeps
 * fitting the screen, as Create role does on Access. The dialog sits inside the wizard's form,
 * and forms do not nest, so Enter in a link saves the links instead of continuing.
 */
function BasicsLegalChoice({ settings }: { settings: AdminBrandingSettings }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const root = useRef<HTMLDivElement>(null)
  const fields = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(() => legalDraftOf(settings))
  const [errors, setErrors] = useState<LegalErrors>({})
  const [failure, setFailure] = useState("")
  const [saving, setSaving] = useState(false)

  function show() {
    setDraft(legalDraftOf(settings))
    setErrors({})
    setFailure("")
    setOpen(true)
  }

  function change(patch: Partial<LegalDraft>) {
    setDraft((current) => ({ ...current, ...patch }))
    // Editing a link takes its error away until the next Save checks it again.
    setErrors((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => !(key in patch))),
    )
  }

  async function save() {
    if (saving) return
    const found = legalErrors(draft)
    flushSync(() => {
      setErrors(found)
      setFailure("")
    })
    if (Object.keys(found).length > 0) {
      fields.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return
    }
    // Found now, while this section is on the page: once legal is ready the section goes. As
    // FormWizard itself falls back: the step's heading, or in tabs (editing) the open panel.
    const stepTarget =
      root.current?.closest("form")?.querySelector<HTMLElement>(".tny-wizard-step-title") ??
      root.current?.closest<HTMLElement>('[role="tabpanel"]')
    setSaving(true)
    try {
      // Everything else as saved: only the legal choice and its links change.
      const saved = await updateAdminBranding({
        backgroundUrl: settings.backgroundUrl,
        groupLogoUrl: settings.groupLogoUrl,
        groupName: settings.groupName,
        primaryColor: settings.primaryColor,
        secondaryColor: settings.secondaryColor,
        sidebarLinks: settings.sidebarLinks,
        // The same rule as Settings: with the standard terms, the saved links stay.
        ...legalSaveFields(draft, settings, true),
        standardLegal: draft.standardLegal,
      })
      queryClient.setQueryData(["admin-settings"], saved)
      // The terms and privacy are on the dashboard's setup checklist (G7).
      void queryClient.invalidateQueries({ queryKey: ["admin-setup"] })
      await queryClient.invalidateQueries({ queryKey: ["admin-products"] })
      toast({ message: "Terms and privacy saved.", tone: "success" })
      // Closed now, so focus returns to the button that opened it, then, with legal ready and
      // this section about to go, to the step it was in.
      flushSync(() => setOpen(false))
      if (queryClient.getQueryData<AdminProductCatalog>(["admin-products"])?.legal.ready) {
        stepTarget?.focus()
      }
    } catch (error) {
      setFailure(error instanceof Error ? error.message : "Branding could not be saved.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="admin-actions" ref={root}>
      <p className="admin-note">Set your terms and privacy to publish a signup page.</p>
      <Button onClick={show} size="sm" variant="secondary">
        Set terms and privacy
      </Button>
      {open ? (
        <Dialog
          // While the request runs nothing dismisses the dialog, so its answer always lands here.
          // The buttons are busy, not disabled, so focus never drops out of the dialog.
          footer={
            <>
              <Button
                aria-disabled={saving || undefined}
                onClick={saving ? undefined : () => setOpen(false)}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                aria-busy={saving || undefined}
                aria-disabled={saving || undefined}
                onClick={() => void save()}
              >
                {saving ? <span aria-hidden="true" className="tny-spinner" /> : null}
                {draft.standardLegal ? "Use standard terms" : "Save"}
              </Button>
            </>
          }
          onClose={() => setOpen(false)}
          open
          pending={saving}
          title="Terms and privacy"
        >
          {/* Enter in a link saves, as the wizard's form would otherwise take it as Continue. */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: it only routes Enter from the inputs inside */}
          <div
            className="admin-stack"
            onKeyDown={(event) => {
              const { target } = event
              // The Enter that ends an IME composition picks a candidate; it is not a save.
              if (event.key !== "Enter" || event.nativeEvent.isComposing) return
              if (!(target instanceof HTMLInputElement)) return
              event.preventDefault()
              // On the choice, Enter neither saves nor continues: Space changes it.
              if (target.type !== "checkbox") void save()
            }}
            ref={fields}
          >
            <LegalLinks
              companyName={settings.groupName}
              draft={draft}
              errors={errors}
              onChange={change}
              // A signup page needs both links (hasSignupLegalLinks), so both are required here.
              privacyRequired
              standardAvailable
            />
            {failure ? (
              <p className="tny-error" role="alert">
                {failure}
              </p>
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </div>
  )
}

function BasicsStep({ draft, errors, setDraft, catalog, editing }: StepProps) {
  const { shown, clear } = useEditedErrors(errors)
  const free = draft.pricingType === "free"
  // A paid product's name is its Stripe product's, which a published price keeps.
  const nameLocked = editing?.kind === "paid"
  return (
    <div className="admin-stack">
      <div className="admin-field-grid">
        <Field
          error={shown("name")}
          hint={nameLocked ? "A published paid product keeps its Stripe name." : undefined}
          label="Product name"
          required
        >
          <Input
            autoComplete="off"
            disabled={nameLocked}
            onChange={(event) => {
              const name = event.currentTarget.value
              clear("name", ...(draft.slugEdited ? [] : ["slug"]))
              setDraft((current) => ({
                ...current,
                name,
                slug: current.slugEdited ? current.slug : slugFromName(name),
              }))
            }}
            value={draft.name}
          />
        </Field>
        {free ? (
          <Field
            error={shown("slug")}
            hint="Lowercase letters, numbers and hyphens, such as northwind-community."
            label="Signup link"
            required
          >
            {/* The address before the slug (signupUrl's own path) sits in the field's frame, so
                the link reads as one line. Review shows it in full. */}
            <Input
              autoCapitalize="none"
              autoComplete="off"
              onChange={(event) => {
                clear("slug")
                setDraft({ slug: event.currentTarget.value, slugEdited: true })
              }}
              prefix={`…${signupPathPrefix}`}
              spellCheck={false}
              value={draft.slug}
            />
          </Field>
        ) : (
          <Field
            error={shown("slug")}
            hint="Optional. With one, only people who have the ?password= link see the product."
            label="Storefront password"
          >
            <Input
              autoCapitalize="none"
              autoComplete="off"
              onChange={(event) => {
                clear("slug")
                setDraft({ slug: event.currentTarget.value, slugEdited: true })
              }}
              spellCheck={false}
              value={draft.slugEdited ? draft.slug : ""}
            />
          </Field>
        )}
      </div>
      {free ? (
        <Field
          error={shown("description")}
          hint="Shown on the signup page, up to 2,000 characters."
          label="Description"
        >
          <Textarea
            onChange={(event) => {
              clear("description")
              setDraft({ description: event.currentTarget.value })
            }}
            rows={3}
            value={draft.description}
          />
        </Field>
      ) : null}
      {free && !catalog.legal.ready && catalog.legal.standardAvailable ? <BasicsLegal /> : null}
      {errors.legal && !legalSettled(catalog) ? <LegalLinksError message={errors.legal} /> : null}
    </div>
  )
}

function PricingStep({ draft, errors, setDraft, catalog, editing }: StepProps) {
  const { shown, clear } = useEditedErrors(errors)
  const errorId = useId()
  const error = shown("pricingType")
  if (editing) {
    // Published, the choice is fixed: a summary, not three disabled cards.
    const chosen = PRICING.find((option) => option.value === draft.pricingType)
    return (
      <p className="admin-locked">
        <NavIcon name="lock" />
        {chosen?.label} · can’t change after publishing
      </p>
    )
  }
  const freeNeedsDiscord = !catalog.freeWritable && catalog.guilds.length === 0
  return (
    <div className="admin-stack">
      <Alert>How members pay can’t change once a product is published.</Alert>
      <fieldset className="admin-fieldset">
        <legend className="tny-label">How do members pay?</legend>
        <div className="admin-choice-grid">
          {PRICING.map((option) => (
            <Radio
              aria-describedby={error ? errorId : undefined}
              aria-invalid={error && draft.pricingType === option.value ? true : undefined}
              card
              checked={draft.pricingType === option.value}
              disabled={option.value === "free" ? !catalog.freeWritable : !catalog.writable}
              hint={option.hint}
              key={option.value}
              label={option.label}
              name="pricingType"
              onChange={() => {
                clear("pricingType")
                setDraft((current) => withPricing(current, option.value))
              }}
              value={option.value}
            />
          ))}
        </div>
      </fieldset>
      {error ? (
        <p className="tny-error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
      {freeNeedsDiscord || !catalog.writable ? (
        <p className="admin-note admin-inline-links">
          {freeNeedsDiscord ? (
            <>
              Free needs a Discord server: <ConnectDiscordLink />.
            </>
          ) : null}
          {freeNeedsDiscord && !catalog.writable ? " " : null}
          {catalog.writable ? null : (
            <>
              One-time and Recurring need Stripe: <Link to="/dashboard/stripe">connect it</Link>.
            </>
          )}
        </p>
      ) : null}
    </div>
  )
}

type TextKey = "price" | "intervalCount" | "initialPayment" | "trialDays"

/** An Input's value and onChange for a text field of the draft, clearing its error on edit. */
function textField(
  draft: ProductDraft,
  setDraft: StepProps["setDraft"],
  clear: (key: string) => void,
  key: TextKey,
) {
  return {
    onChange(event: { currentTarget: HTMLInputElement }) {
      clear(key)
      setDraft({ [key]: event.currentTarget.value })
    },
    value: draft[key],
  }
}

// Stripe prices are fixed, so a published product keeps its amount, currency and period.
const PRICE_LOCKED =
  "A published price can’t change in Stripe. For a new price, create a new product."

function PriceStep({ draft, errors, setDraft, catalog, editing }: StepProps) {
  const { shown, clear } = useEditedErrors(errors)
  const locked = Boolean(editing)
  const existing = catalog.providerProducts.find(({ id }) => id === draft.existingProductId)
  return (
    <div className="admin-stack">
      {!editing && catalog.providerProducts.length > 0 ? (
        <Field
          hint={
            existing
              ? `The product takes its Stripe name, ${existing.name}.`
              : "A product you already sell in Stripe, or a new one."
          }
          label="Stripe product"
        >
          <Select
            onChange={(event) => {
              const existingProductId = event.currentTarget.value
              const existing = catalog.providerProducts.find(({ id }) => id === existingProductId)
              setDraft((current) => ({
                ...current,
                existingProductId,
                name: existing?.name ?? current.name,
              }))
            }}
            value={draft.existingProductId}
          >
            <option value="">New product</option>
            {catalog.providerProducts.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <div className="admin-field-pair">
        <Field error={shown("price")} label="Price" required>
          <Input
            autoComplete="off"
            disabled={locked}
            inputMode="decimal"
            prefix={currencySymbol(draft.currency)}
            size="short"
            {...textField(draft, setDraft, clear, "price")}
          />
        </Field>
        <Field label="Currency">
          <Select
            disabled={locked}
            onChange={(event) => setDraft({ currency: event.currentTarget.value })}
            value={draft.currency}
          >
            {ogAdminCurrencies.map((currency) => (
              <option key={currency}>{currency}</option>
            ))}
          </Select>
        </Field>
      </div>
      {locked ? <p className="admin-note">{PRICE_LOCKED}</p> : null}
    </div>
  )
}

function BillingStep({ draft, errors, setDraft, editing }: StepProps) {
  const { shown, clear } = useEditedErrors(errors)
  const locked = Boolean(editing)
  return (
    <div className="admin-stack">
      <div className="admin-field-pair">
        <Field error={shown("intervalCount")} label="Bill every" required>
          <Input
            disabled={locked}
            inputMode="numeric"
            {...textField(draft, setDraft, clear, "intervalCount")}
          />
        </Field>
        <Field label="Period">
          <Select
            disabled={locked}
            onChange={(event) =>
              setDraft({ interval: event.currentTarget.value as ProductDraft["interval"] })
            }
            value={draft.interval}
          >
            {INTERVALS.map((interval) => (
              <option key={interval.value} value={interval.value}>
                {numberValue(draft.intervalCount) === 1 ? interval.one : interval.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="admin-field-pair">
        <Field error={shown("initialPayment")} hint="Once, at signup." label="Initial payment">
          <Input
            autoComplete="off"
            disabled={locked}
            inputMode="decimal"
            prefix={currencySymbol(draft.currency)}
            size="short"
            {...textField(draft, setDraft, clear, "initialPayment")}
          />
        </Field>
        <Field error={shown("trialDays")} hint="0 for none." label="Trial days">
          <Input
            inputMode="numeric"
            size="short"
            suffix="days"
            {...textField(draft, setDraft, clear, "trialDays")}
          />
        </Field>
      </div>
      {locked ? <p className="admin-note">{PRICE_LOCKED}</p> : null}
    </div>
  )
}

type Role = AdminProductGuild["roles"][number]

/**
 * The roles as options. A role the bot cannot give (G4) is disabled, with how to fix it: drag the
 * bot's highest role above it. That role is named only when Discord said its name, because a
 * business on its own Discord app has a bot role that is not called Ternary.
 */
function roleOptions(
  roles: { assignable?: boolean; id: string; name: string }[],
  botRole?: string,
) {
  return roles.map((role) =>
    role.assignable === false
      ? {
          description: (
            <>
              Ternary can't give this role. In Discord open Server Settings → Roles and drag{" "}
              {botRole ? <strong>{botRole}</strong> : "the Ternary bot's role"} above{" "}
              <strong>{role.name}</strong>.
            </>
          ),
          disabled: true,
          label: role.name,
          value: role.id,
        }
      : { label: role.name, value: role.id },
  )
}

/**
 * The options, then one for each chosen role the server no longer lists, named by its id, so it
 * can be unticked: a role deleted in Discord otherwise stays on the product, hidden in the "+N",
 * and its warning cannot be cleared. Unticked, it leaves the list. Only while Discord lists the
 * server's roles: with none listed (the bot cannot read the server) nothing is known to be gone.
 */
function withDeletedRoles(options: ReturnType<typeof roleOptions>, chosen: string[]) {
  if (options.length === 0) return options
  const listed = new Set(options.map((option) => option.value))
  const deleted = [...new Set(chosen)].filter((id) => !listed.has(id))
  return [
    ...options,
    ...deleted.map((id) => ({
      description: "It no longer exists in Discord. Untick it.",
      label: `Deleted role ${id}`,
      value: id,
    })),
  ]
}

/** How many of the server's roles the bot cannot give. */
function outOfReach(guild: AdminProductGuild | undefined) {
  return guild?.roles.filter((role) => role.assignable === false).length ?? 0
}

/** The catalog with a role just created in a server, unless it lists it already. */
function withRole(catalog: AdminProductCatalog, guildId: string, role: Role) {
  return {
    ...catalog,
    guilds: catalog.guilds.map((guild) =>
      guild.id === guildId && !guild.roles.some(({ id }) => id === role.id)
        ? { ...guild, roles: [...guild.roles, role] }
        : guild,
    ),
  }
}

function FreeAccess({ draft, errors, setDraft, catalog }: StepProps) {
  const { shown, clear } = useEditedErrors(errors)
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const step = useRef<HTMLDivElement>(null)
  const roleTools = useRef<HTMLDivElement>(null)
  const [checking, setChecking] = useState(false)
  const latestLoad = useRef(0)
  // The server on screen now, which a Check again's answer must still be about.
  const shownGuild = useRef(draft.guildId)
  useEffect(() => {
    shownGuild.current = draft.guildId
  }, [draft.guildId])

  // Check again and Create role load the catalog themselves, so a failed load keeps the step (a
  // failed query would swap the whole wizard for its error) and the roles it shows. Only the
  // newest load counts: an older one that answers last is dropped (null), so it neither replaces
  // the newer list nor says anything about it.
  async function loadCatalog() {
    const load = ++latestLoad.current
    const fresh = await loadAdminProducts()
    if (load !== latestLoad.current) return null
    queryClient.setQueryData(["admin-products"], fresh)
    return fresh
  }

  async function checkAgain() {
    if (checking) return
    setChecking(true)
    try {
      const fresh = await loadCatalog()
      // A newer load, or another server chosen while it loaded: this answer is about nothing on
      // screen, so it says nothing.
      if (!fresh || shownGuild.current !== draft.guildId) return
      // The server left the list, or the bot can no longer reach it: it was not checked, so say
      // why (the words Continue would use) and put focus on the server to choose another.
      const lost = validateAccess(draft, fresh).guildId
      if (lost) {
        toast({ message: lost, tone: "error" })
        step.current?.querySelector("select")?.focus()
        return
      }
      const still = outOfReach(fresh.guilds.find((each) => each.id === draft.guildId))
      if (still === 0) {
        // Every role is in reach, so Check again goes: focus moves to Create role beside it.
        roleTools.current?.querySelector<HTMLElement>("button, input")?.focus()
      } else {
        // Check again stays, with focus: say that nothing changed, or the press looks lost.
        toast({ message: `Ternary still can't give ${still} role${still === 1 ? "" : "s"}.` })
      }
    } catch {
      toast({ message: "The roles could not load. Try again.", tone: "error" })
    } finally {
      setChecking(false)
    }
  }

  function chooseCreated(guildId: string, role: Role) {
    clear("roleIds")
    setDraft((current) => ({
      ...current,
      noRoles: false,
      roleIds: [...current.roleIds.filter((id) => id !== role.id), role.id],
    }))
    // The new role is an option at once; the reload brings the server's own list.
    queryClient.setQueryData<AdminProductCatalog>(
      ["admin-products"],
      (current) => current && withRole(current, guildId, role),
    )
    toast({ message: `${role.name} was created and chosen.`, tone: "success" })
    void loadCatalog().catch(() => {
      // The created role is already in the cache, so a failed reload changes nothing shown.
    })
  }

  if (catalog.guilds.length === 0) {
    return (
      <>
        <EmptyState
          action={
            <Link className="tny-button tny-button-primary" to="/dashboard/discord">
              Connect Discord
            </Link>
          }
          description="Members join your Discord server when they sign up. Connect it, then come back: this draft is kept."
          title="Connect Discord first"
        />
        {shown("guildId") ? (
          <p className="tny-error" role="alert">
            {shown("guildId")}
          </p>
        ) : null}
      </>
    )
  }
  const guild = catalog.guilds.find((each) => each.id === draft.guildId)
  const roles = guild?.roles ?? []
  // With no roles to pick, the role error belongs on the checkbox that grants none.
  const roleError = shown("roleIds")
  return (
    <div className="admin-stack" ref={step}>
      <Field
        error={shown("guildId")}
        hint={
          guild && isReachable(guild) && roles.length === 0
            ? "This server has no roles the bot can grant yet."
            : "Members join this server when they sign up."
        }
        label="Discord server"
        required
      >
        <Select
          onChange={(event) => {
            clear("guildId", "roleIds")
            setDraft({ guildId: event.currentTarget.value, roleIds: [] })
          }}
          value={guild ? draft.guildId : ""}
        >
          {guild ? null : <option value="">Choose a server</option>}
          {/* A server the bot cannot reach is listed, plainly, but cannot be chosen. */}
          {catalog.guilds.map((each) => (
            <option disabled={!isReachable(each)} key={each.id} value={each.id}>
              {each.name || each.id}
              {isReachable(each) ? "" : " (Bot not reachable)"}
            </option>
          ))}
        </Select>
      </Field>
      {/* The way out when the chosen server, or every server, is out of the bot's reach. */}
      {(guild && !isReachable(guild)) || !catalog.guilds.some(isReachable) ? (
        <p className="admin-note admin-inline-links">
          To bring the bot back to {guild ? guild.name || "this server" : "your servers"}:{" "}
          <ConnectDiscordLink />.
        </p>
      ) : null}
      <MultiSelect
        disabled={draft.noRoles || roles.length === 0}
        error={roles.length > 0 ? roleError : undefined}
        hint="Members get these roles when they join."
        label="Roles"
        onChange={(roleIds) => {
          clear("roleIds")
          setDraft({ roleIds })
        }}
        options={withDeletedRoles(
          roleOptions(roles, guild?.botRole),
          draft.noRoles ? [] : draft.roleIds,
        )}
        required={!draft.noRoles}
        value={draft.noRoles ? [] : draft.roleIds}
      />
      {guild && isReachable(guild) ? (
        <div className="admin-actions" ref={roleTools}>
          <CreateRoleField
            disabled={!catalog.freeWritable}
            guildId={guild.id}
            onCreated={(role) => chooseCreated(guild.id, role)}
            productName={draft.name}
          />
          {outOfReach(guild) > 0 ? (
            // Busy but never disabled while it loads, as ConfirmDialog's pending buttons are: a
            // disabled button would drop focus to the page.
            <Button
              aria-busy={checking || undefined}
              aria-disabled={checking || undefined}
              onClick={() => void checkAgain()}
              size="sm"
              variant="secondary"
            >
              {checking ? <span aria-hidden="true" className="tny-spinner" /> : null}
              Check again
            </Button>
          ) : null}
        </div>
      ) : null}
      <Checkbox
        checked={draft.noRoles}
        error={roles.length === 0 ? roleError : undefined}
        hint="For a server you only track: members join with no role."
        label="Don’t grant any roles"
        onChange={(event) => {
          const noRoles = event.currentTarget.checked
          clear("roleIds")
          setDraft((current) => ({ ...current, noRoles, roleIds: noRoles ? [] : current.roleIds }))
        }}
      />
    </div>
  )
}

function PaidAccess({ draft, errors, setDraft, catalog }: StepProps) {
  const { shown, clear } = useEditedErrors(errors)
  const primary = catalog.guilds.find((guild) => guild.primary)
  const discord = draft.productType === "discord"
  const trial = draft.pricingType === "recurring" && numberValue(draft.trialDays) > 0
  const options = roleOptions(catalog.roles)
  return (
    <div className="admin-stack">
      <div className="admin-field-grid">
        <div className="tny-field">
          <span className="tny-label">Discord server</span>
          <span className="admin-readonly">{primary?.name || "Your connected server"}</span>
          {/* The API's word on the server or its bot (accessError) has no control to sit on. */}
          {shown("guildId") ? (
            <p className="tny-error admin-inline-links" role="alert">
              {shown("guildId")} <ConnectDiscordLink />
            </p>
          ) : null}
        </div>
        <Field label="Delivery">
          <Select
            onChange={(event) => {
              const productType = event.currentTarget.value as ProductDraft["productType"]
              clear("roleIds", "removalRoleIds")
              setDraft({ productType })
            }}
            value={draft.productType}
          >
            <option value="discord">Discord roles</option>
            <option value="manual">Manual fulfilment</option>
          </Select>
        </Field>
      </div>
      {discord ? (
        <>
          <div className="admin-field-grid">
            <MultiSelect
              error={shown("roleIds")}
              label="Roles"
              onChange={(roleIds) => {
                clear("roleIds")
                setDraft({ roleIds })
              }}
              options={withDeletedRoles(options, draft.roleIds)}
              required
              value={draft.roleIds}
            />
            {trial ? (
              <MultiSelect
                hint="Granted during the free trial."
                label="Trial roles"
                onChange={(trialRoleIds) => setDraft({ trialRoleIds })}
                options={withDeletedRoles(options, draft.trialRoleIds)}
                value={draft.trialRoleIds}
              />
            ) : null}
          </div>
          <div className="admin-field-grid">
            <Field label="When access ends">
              <Select
                onChange={(event) => {
                  clear("removalRoleIds")
                  setDraft({ removal: event.currentTarget.value as ProductDraft["removal"] })
                }}
                value={draft.removal}
              >
                <option value="kick">Remove from the server</option>
                <option value="roles">Remove roles only</option>
              </Select>
            </Field>
            {draft.removal === "roles" ? (
              <MultiSelect
                error={shown("removalRoleIds")}
                label="Roles to remove"
                onChange={(removalRoleIds) => {
                  clear("removalRoleIds")
                  setDraft({ removalRoleIds })
                }}
                options={withDeletedRoles(options, draft.removalRoleIds)}
                required
                value={draft.removalRoleIds}
              />
            ) : null}
          </div>
        </>
      ) : (
        <p className="admin-note">You deliver this product yourself, so no role is granted.</p>
      )}
    </div>
  )
}

function AccessStep(props: StepProps) {
  return props.draft.pricingType === "free" ? <FreeAccess {...props} /> : <PaidAccess {...props} />
}

function FieldsStep({ draft, errors, setDraft }: StepProps) {
  const { shown, clear } = useEditedErrors(errors)
  return (
    <SignupFieldsEditor
      error={shown("signupFields")}
      onChange={(signupFields) => {
        clear("signupFields")
        setDraft({ signupFields })
      }}
      value={draft.signupFields}
    />
  )
}

/** The API's refusal of a paid product while the business is on Basic, the free plan. */
const UPGRADE_NEEDED = "Upgrade before selling paid products."

/** The paid products a product can link to as upgrades or add-ons: never itself. */
function linkableProducts(catalog: AdminProductCatalog, editing: Editing | null) {
  const own = editing?.kind === "paid" ? editing.product.planId : null
  return catalog.products
    .filter((product) => product.planId !== own)
    .map((product) => ({
      label: `${product.name}, ${paidPriceLabel(product.priceMinor, product.currency, product.interval, product.intervalCount)}`,
      value: product.planId,
    }))
}

function ExtrasStep({ draft, errors, setDraft, catalog, editing }: StepProps) {
  const { shown, clear } = useEditedErrors(errors)
  const linkable = linkableProducts(catalog, editing)
  return (
    <div className="admin-stack">
      <Field hint="One per line, shown on your storefront." label="Features">
        <Textarea
          onChange={(event) => setDraft({ features: event.currentTarget.value })}
          rows={3}
          value={draft.features}
        />
      </Field>
      <div className="admin-field-grid admin-field-grid-3">
        <Field error={shown("stock")} hint="How many can be sold." label="Stock" required>
          <Input
            inputMode="numeric"
            onChange={(event) => {
              clear("stock")
              setDraft({ stock: event.currentTarget.value })
            }}
            value={draft.stock}
          />
        </Field>
        <MultiSelect
          hint="Products members can switch to."
          label="Upgrades"
          onChange={(upgradeTo) => setDraft({ upgradeTo })}
          options={linkable}
          value={draft.upgradeTo}
        />
        <MultiSelect
          hint="Products members can add."
          label="Add-ons"
          onChange={(addOns) => setDraft({ addOns })}
          options={linkable}
          value={draft.addOns}
        />
      </div>
    </div>
  )
}

/** "1 upgrade", "3 upgrades": a count with its noun, which takes an s for any count but 1. */
function counted(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`
}

function names(ids: string[], roles: { id: string; name: string }[]) {
  return ids.map((id) => roles.find((role) => role.id === id)?.name ?? id).join(", ")
}

/** Every value of the draft, each with an Edit that opens its step. */
function ReviewStep({ draft, errors, goTo, catalog, editing }: StepProps) {
  const rows: { label: string; value: ReactNode; step: string }[] = []
  if (draft.pricingType === "free") {
    const guild = catalog.guilds.find((each) => each.id === draft.guildId)
    rows.push(
      { label: "Name", step: "basics", value: draft.name },
      {
        label: "Signup link",
        step: "basics",
        // Breaks only after its slashes and dots, never inside the slug.
        value: breakable(signupUrl(catalog.storefrontUrl, draft.slug)),
      },
      { label: "Description", step: "basics", value: draft.description.trim() || "None" },
      { label: "Pricing", step: "pricing", value: "Free" },
      { label: "Discord server", step: "access", value: guild?.name || draft.guildId },
      {
        label: "Roles",
        step: "access",
        value: draft.noRoles
          ? "None: members join with no role"
          : names(draft.roleIds, guild?.roles ?? []),
      },
      {
        label: "Signup fields",
        step: "fields",
        value:
          draft.signupFields.length === 0
            ? "None: name and email only"
            : `${draft.signupFields.map((field) => field.label).join(", ")} (${counted(draft.signupFields.length, "field")})`,
      },
    )
  } else {
    const recurring = draft.pricingType === "recurring"
    const priceMinor = Math.round(Number(draft.price || "0") * 100)
    const features = lines(draft.features).length
    const discord = draft.productType === "discord"
    const extras = [
      features > 0 ? counted(features, "feature") : null,
      `stock ${draft.stock}`,
      draft.upgradeTo.length ? counted(draft.upgradeTo.length, "upgrade") : null,
      draft.addOns.length ? counted(draft.addOns.length, "add-on") : null,
    ]
    const stripe = catalog.providerProducts.find(({ id }) => id === draft.existingProductId)
    rows.push(
      { label: "Name", step: "basics", value: draft.name },
      {
        label: "Storefront password",
        step: "basics",
        value: (draft.slugEdited && draft.slug.trim()) || "None",
      },
      {
        label: "Price",
        step: "price",
        // With the Stripe product it goes on, so the paid Review fits a phone screen.
        value: `${paidPriceLabel(
          priceMinor,
          draft.currency,
          recurring ? draft.interval : "one_time",
          numberValue(draft.intervalCount),
        )}, on ${stripe ? `Stripe product ${stripe.name}` : "a new Stripe product"}`,
      },
      ...(recurring
        ? [
            {
              label: "Billing",
              step: "billing",
              value:
                [
                  numberValue(draft.trialDays) > 0
                    ? `${numberValue(draft.trialDays)}-day trial`
                    : null,
                  draft.initialPayment.trim()
                    ? `${formatMoney(Math.round(Number(draft.initialPayment) * 100), draft.currency)} at signup`
                    : null,
                ]
                  .filter(Boolean)
                  .join(", ") || "No trial or initial payment",
            },
          ]
        : []),
      {
        // Named after its step: delivery and the roles are Access's choices.
        label: "Access",
        step: "access",
        // The trial roles go with the roles, so the longest paid Review fits a phone screen.
        value: discord
          ? `${names(draft.roleIds, catalog.roles)}${
              recurring && numberValue(draft.trialDays) > 0
                ? ` (${names(draft.trialRoleIds, catalog.roles) || "none"} during the trial)`
                : ""
            }`
          : "Manual fulfilment",
      },
      ...(discord
        ? [
            {
              label: "When access ends",
              step: "access",
              value:
                draft.removal === "kick"
                  ? "Removed from the server"
                  : `Roles removed: ${names(draft.removalRoleIds, catalog.roles)}`,
            },
          ]
        : []),
      { label: "Extras", step: "extras", value: extras.filter(Boolean).join(", ") },
    )
  }
  return (
    <div className="admin-stack">
      <dl className="admin-review">
        {rows.map((row) => (
          <div className="admin-review-row" key={row.label}>
            <dt className="admin-review-term">{row.label}</dt>
            <dd className="admin-review-value">{row.value}</dd>
            <dd className="admin-review-edit">
              <Button onClick={() => goTo(row.step)} size="sm" variant="ghost">
                {/* The space is outside the hidden text, where a name keeps it. */}
                {"Edit "}
                <span className="tny-visually-hidden">{row.label}</span>
              </Button>
            </dd>
          </div>
        ))}
      </dl>
      {errors.legal && !editing && !legalSettled(catalog) ? (
        <LegalLinksError message={errors.legal} />
      ) : null}
    </div>
  )
}

/**
 * The Access step's field for a refusal that belongs to it (A's save path): a server that is not
 * proven (400), roles the server does not have or the bot cannot give (400), or its bot gone (503).
 */
function accessError(error: AdminApiError): WizardErrors | null {
  if (error.status === 503 && /discord bot/i.test(error.message)) return { guildId: error.message }
  if (error.status !== 400) return null
  if (/discord servers?/i.test(error.message)) return { guildId: error.message }
  if (/discord roles?|ternary role/i.test(error.message)) return { roleIds: error.message }
  return null
}

/**
 * "← Products", back to the list: PageHeader's back link, or above the wizard's own title. Above
 * the wizard it borrows PageHeader's `.tny-page-header-back` wrapper for the same look, so a change
 * to PageHeader's back-link markup must change this too.
 */
function ProductsBackLink({ bare = false }: { bare?: boolean }) {
  const link = (
    <Link to="/dashboard/products">
      <NavIcon name="back" />
      Products
    </Link>
  )
  return bare ? link : <div className="tny-page-header-back">{link}</div>
}

function ProductWizard({
  catalog,
  editing,
}: {
  catalog: AdminProductCatalog
  editing: Editing | null
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [params, setParams] = useSearchParams()
  const [initialDraft] = useState(() => {
    if (editing?.kind === "free") return draftFromFreeProduct(catalog, editing.product)
    if (editing?.kind === "paid") return draftFromPaidProduct(catalog, editing.product)
    return newProductDraft(catalog)
  })
  // The URL follows the wizard's step (onStepChange), and Basics opens without one.
  const onFirstStep = (params.get("step") ?? "basics") === "basics"
  const takenSlugs = catalog.freeProducts
    .filter((product) => product.id !== editing?.product.id)
    .map((product) => product.slug)
  const step =
    (render: (props: StepProps) => ReactNode) => (context: WizardStepContext<ProductDraft>) =>
      render({ ...context, catalog, editing })

  const steps: WizardStep<ProductDraft>[] = [
    {
      id: "basics",
      render: step((props) => <BasicsStep {...props} />),
      title: "Basics",
      validate: (draft) => validateBasics(draft, takenSlugs),
    },
    {
      // Choosing is for a new product: published, the tab is a locked summary with no Save.
      description: editing ? undefined : "Free products publish without Stripe.",
      id: "pricing",
      readOnly: Boolean(editing),
      render: step((props) => <PricingStep {...props} />),
      title: "Pricing",
      validate: (draft) => validatePricing(draft, catalog, Boolean(editing)),
    },
    {
      id: "price",
      // A published price is fixed in Stripe, so its tab has nothing to save.
      readOnly: Boolean(editing),
      render: step((props) => <PriceStep {...props} />),
      skip: (draft) => draft.pricingType === "free",
      title: "Price",
      // A published price is locked, and a legacy one may be $0, which the contract allows.
      validate: (draft) => (editing ? {} : validatePrice(draft)),
    },
    {
      description: "How often members pay, and what they pay first.",
      id: "billing",
      render: step((props) => <BillingStep {...props} />),
      skip: (draft) => draft.pricingType !== "recurring",
      title: "Billing",
      validate: (draft) => validateBilling(draft, Boolean(editing)),
    },
    {
      id: "access",
      // A free product with no server has one way on, Connect Discord, which is then the primary.
      nextVariant: (draft) =>
        draft.pricingType === "free" && catalog.guilds.length === 0 ? "secondary" : "primary",
      render: step((props) => <AccessStep {...props} />),
      title: "Access",
      validate: (draft) =>
        validateAccess(
          draft,
          catalog,
          editing?.kind === "free" ? editing.product.access.roleIds : [],
        ),
    },
    {
      id: "fields",
      render: step((props) => <FieldsStep {...props} />),
      skip: (draft) => draft.pricingType !== "free",
      title: "Signup fields",
      validate: validateFields,
    },
    {
      id: "extras",
      render: step((props) => <ExtrasStep {...props} />),
      skip: (draft) => draft.pricingType === "free",
      title: "Extras",
      validate: validateExtras,
    },
    {
      id: "review",
      render: step((props) => <ReviewStep {...props} />),
      skip: () => Boolean(editing),
      title: "Review",
    },
  ]

  async function save(draft: ProductDraft) {
    // The wizard checks only the step it leaves, and Back can change an earlier one's meaning (a
    // paid password carried back to Free), so Publish checks every step that applies first.
    for (const each of steps) {
      if (each.skip?.(draft)) continue
      const errors = each.validate?.(draft) ?? {}
      if (Object.values(errors).some(Boolean)) throw new WizardFieldError(each.id, errors)
    }
    const free = draft.pricingType === "free"
    try {
      if (!editing) {
        await createAdminProduct(free ? freeRequest(draft) : paidRequest(draft))
      } else if (free) {
        await updateAdminProduct(editing.product.id, freeRequest(draft))
      } else {
        const { existingProductId: _created, ...update } = paidRequest(draft)
        await updateAdminProduct(editing.product.id, update)
      }
    } catch (error) {
      if (error instanceof AdminApiError && free && error.status === 409) {
        throw new WizardFieldError("basics", { slug: SLUG_TAKEN })
      }
      if (
        error instanceof AdminApiError &&
        error.status === 400 &&
        /terms|privacy/i.test(error.message)
      ) {
        throw new WizardFieldError(editing ? "basics" : "review", { legal: error.message })
      }
      // The server, its roles or its bot: the API's word on what Access chose.
      const access = error instanceof AdminApiError ? accessError(error) : null
      if (access) throw new WizardFieldError("access", access)
      throw error
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin-products"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] }),
      // The first product is on the dashboard's setup checklist (G7).
      queryClient.invalidateQueries({ queryKey: ["admin-setup"] }),
    ])
    const name = draft.name.trim()
    if (editing) toast({ message: `${name} saved.`, tone: "success" })
    else if (!free) toast({ message: `${name} is published.`, tone: "success" })
    else {
      // The link is in the products table; the toast offers to copy it, and stays until used or
      // dismissed.
      const url = signupUrl(catalog.storefrontUrl, draft.slug)
      toast({
        action: { label: "Copy link", onClick: () => void copySignupLink(url) },
        message: `${name} is live.`,
        tone: "success",
      })
    }
    navigate("/dashboard/products")
  }

  async function copySignupLink(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      toast({ message: "Signup link copied.", tone: "success" })
    } catch {
      toast({ message: `The link could not be copied: ${url}`, tone: "error" })
    }
  }

  return (
    <div className="admin-page admin-product-wizard">
      <ProductsBackLink />
      <FormWizard<ProductDraft>
        initialDraft={initialDraft}
        // Back on the first step (and in tabs, always) leaves the wizard, so it says Cancel.
        labels={{ back: editing || onFirstStep ? "Cancel" : "Back" }}
        mode={editing ? "tabs" : "steps"}
        onCancel={() => {
          // Cancel ends a new product: the next Create product starts empty.
          if (!editing) {
            for (const key of [PRODUCT_DRAFT_KEY, `${PRODUCT_DRAFT_KEY}:submitted`]) {
              try {
                sessionStorage.removeItem(key)
              } catch {
                // Blocked storage kept nothing to drop.
              }
            }
          }
          navigate("/dashboard/products")
        }}
        onStepChange={(stepId) =>
          setParams(
            (current) => {
              const next = new URLSearchParams(current)
              next.set("step", stepId)
              return next
            },
            { replace: true },
          )
        }
        onSubmit={save}
        persistKey={editing ? undefined : PRODUCT_DRAFT_KEY}
        // P3: Basic's refusal of a paid product says where its upgrade is.
        renderError={(message) => (
          <p className="tny-error" role="alert">
            {message}
            {message === UPGRADE_NEEDED ? (
              <>
                {" "}
                <Link to="/dashboard/account">Upgrade your plan</Link>
              </>
            ) : null}
          </p>
        )}
        stepId={params.get("step") ?? undefined}
        steps={steps}
        submitLabel={editing ? "Save" : "Publish product"}
        title={editing ? `Edit ${editing.product.name}` : "New product"}
      />
    </div>
  )
}

/**
 * `/dashboard/products/new` and `/dashboard/products/:id/edit`. A new product is a FormWizard
 * whose draft and `?step=` survive a reload; an existing one opens in tabs, with one Save.
 */
export function AdminProductWizardPage() {
  const { id } = useParams()
  const query = useQuery({ queryFn: loadAdminProducts, queryKey: ["admin-products"] })
  const title = id ? "Edit product" : "New product"

  if (query.isPending || query.isError) {
    return (
      <section aria-busy={query.isPending || undefined} className="admin-stack admin-page">
        <PageHeader back={<ProductsBackLink bare />} title={title} />
        {query.isPending ? (
          <p className="admin-note" role="status">
            Loading products…
          </p>
        ) : (
          <div className="admin-stack" role="alert">
            <p className="tny-error">Products could not load.</p>
            <div>
              <Button onClick={() => void query.refetch()} variant="secondary">
                Try again
              </Button>
            </div>
          </div>
        )}
      </section>
    )
  }

  const catalog = query.data
  let editing: Editing | null = null
  if (id) {
    const free = catalog.freeProducts.find((product) => product.id === id)
    const paid = catalog.products.find((product) => product.id === id)
    if (free) editing = { kind: "free", product: free }
    else if (paid) editing = { kind: "paid", product: paid }
    else {
      return (
        <section className="admin-stack admin-page">
          <PageHeader back={<ProductsBackLink bare />} title={title} />
          <EmptyState
            action={
              <Link className="tny-button tny-button-secondary" to="/dashboard/products">
                Back to products
              </Link>
            }
            description="It may have been deleted."
            title="Product not found"
          />
        </section>
      )
    }
  }
  return <ProductWizard catalog={catalog} editing={editing} key={id ?? "new"} />
}
